import { useCallback, useEffect, useRef } from 'react';

import { BffApiRequest } from '../../../../../../../../utils/BffApiRequest';

export interface GenerationPollResult<Status extends string = string> {
  status: Status;
  resultPreviewUrl?: string;
  resultFullUrl?: string;
  errorMessage?: string;
  isSuccess?: boolean;
  isFailed?: boolean;
}

interface UseGenerationPollingOptions<
  ResponseType,
  Status extends string = string
> {
  buildShowUrl: (serverId: string) => string;
  mapResponse: (response: ResponseType) => GenerationPollResult<Status>;
  intervalMs: number;
  onTick: (localId: string, result: GenerationPollResult<Status>) => void;
  onSuccess: (localId: string) => void;
}

interface UseGenerationPollingResult {
  startPolling: (localId: string, serverId: string) => void;
  stopPolling: (localId: string) => void;
}

function useGenerationPolling<ResponseType, Status extends string = string>({
  buildShowUrl,
  mapResponse,
  intervalMs,
  onTick,
  onSuccess
}: UseGenerationPollingOptions<
  ResponseType,
  Status
>): UseGenerationPollingResult {
  const intervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map()
  );

  useEffect(() => {
    const intervals = intervalsRef.current;
    return () => {
      intervals.forEach((id) => clearInterval(id));
    };
  }, []);

  const stopPolling = useCallback((localId: string) => {
    const intervalId = intervalsRef.current.get(localId);
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      intervalsRef.current.delete(localId);
    }
  }, []);

  const startPolling = useCallback(
    (localId: string, serverId: string) => {
      const intervalId = setInterval(async () => {
        try {
          const response = await BffApiRequest.getNonStandard<ResponseType>(
            buildShowUrl(serverId)
          );
          const result = mapResponse(response.data);

          onTick(localId, result);

          if (result.isSuccess) {
            onSuccess(localId);
            clearInterval(intervalId);
            intervalsRef.current.delete(localId);
          } else if (result.isFailed) {
            clearInterval(intervalId);
            intervalsRef.current.delete(localId);
          }
        } catch {
          // network error — keep polling
        }
      }, intervalMs);

      intervalsRef.current.set(localId, intervalId);
    },
    [buildShowUrl, intervalMs, mapResponse, onTick, onSuccess]
  );

  return { startPolling, stopPolling };
}

export default useGenerationPolling;
